import { Component, ChangeDetectionStrategy, DestroyRef, Injector, OnInit, inject, runInInjectionContext, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { FieldType, FieldTypeConfig, FormlyModule } from "@ngx-formly/core";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import type { Observable } from "rxjs";

type LoadFn<T> = (ctx: { injector: Injector }) => Observable<T>;

export interface CompetencyOption {
  id: string;
  name: string;
  description?: string;
}

export interface SelectedCompetency {
  matchCompetencyId: string;
  name?: string;
  description?: string;
  sort: number;
}

/**
 * Custom Formly field type for competency selection with ordering.
 * Shows checkboxes on the left and an ordered list on the right.
 * Includes max selection limit and drag-reorder functionality.
 *
 * Usage:
 * ```typescript
 * {
 *   key: 'companyCultureCompetencies',
 *   type: 'competency-picker',
 *   props: {
 *     label: 'Company culture',
 *     maxSelections: 5,
 *     selectPrompt: 'Select maximum 5 competences',
 *     sortPrompt: 'Sort the selected competences based on priority',
 *     options: [{ id: '1', name: 'Teamwork', description: '...' }]
 *   }
 * }
 * ```
 */
@Component({
  selector: "app-formly-competency-picker",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    FormlyModule,
    FontAwesomeModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="competency-picker">
      @if (props.label) {
        <h3>{{ props.label | translate }}</h3>
      }

      <div class="row">
        <!-- Selectable options -->
        <div class="col-12 col-sm-6">
          @if (props['selectPrompt']) {
            <p>{{ props["selectPrompt"] | translate }}</p>
          }

          @for (option of competencyOptions; track option.id) {
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                [id]="'competency-' + option.id"
                [checked]="isSelected(option.id)"
                [disabled]="isDisabled(option.id)"
                (change)="onCheckboxChange($event, option)"
              />
              <label
                class="custom-control-label"
                [for]="'competency-' + option.id"
              >
                {{ option.name | translate }}
              </label>
            </div>
          }
        </div>

        <!-- Selected values with ordering -->
        <div class="col-12 col-sm-6">
          @if (props['sortPrompt']) {
            <p>{{ props["sortPrompt"] | translate }}</p>
          }

          <ul class="list-group">
            @for (
              competency of selectedCompetencies();
              track competency.matchCompetencyId;
              let idx = $index
            ) {
              <li
                class="list-group-item d-flex flex-row justify-content-between"
              >
                <span class="text">
                  {{ idx + 1 }}. {{ competency.name ?? "" | translate }}
                </span>
                <div class="btn-group">
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    (click)="moveUp(idx)"
                    [disabled]="idx === 0"
                  >
                    <fa-icon [icon]="icons.chevronUp"></fa-icon>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    (click)="moveDown(idx)"
                    [disabled]="idx === selectedCompetencies().length - 1"
                  >
                    <fa-icon [icon]="icons.chevronDown"></fa-icon>
                  </button>
                </div>
              </li>
            }
          </ul>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .competency-picker {
        margin-bottom: 1rem;
      }
      .custom-control-label {
        cursor: pointer;
      }
    `,
  ],
})
export class CompetencyPickerFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  private loadedOptions = signal<CompetencyOption[] | null>(null);

  readonly icons = {
    chevronUp: faChevronUp,
    chevronDown: faChevronDown,
  };

  override defaultOptions = {
    props: {
      maxSelections: 5,
      options: [] as CompetencyOption[],
      selectPrompt: "",
      sortPrompt: "",
    },
  };

  ngOnInit(): void {
    const loadFn = this.props['loadOptions'] as LoadFn<CompetencyOption[]> | undefined;
    if (typeof loadFn !== 'function') return;
    runInInjectionContext(this.injector, () => {
      loadFn({ injector: this.injector })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => this.loadedOptions.set(result ?? []));
    });
  }

  /**
   * Plain method (not `computed`) — reads the live FormControl value,
   * which is not a signal. A `computed` here would cache the initial
   * empty list and never refresh when the user ticks a checkbox,
   * because the checkbox change handler doesn't bump any signal the
   * computed reads.
   */
  selectedCompetencies(): SelectedCompetency[] {
    const value = this.formControl.value;
    if (Array.isArray(value)) {
      return [...value].sort((a, b) => a.sort - b.sort);
    }
    return [];
  }

  get competencyOptions(): CompetencyOption[] {
    const loaded = this.loadedOptions();
    if (loaded !== null) return loaded;
    return Array.isArray(this.props.options) ? this.props.options : [];
  }

  isSelected(competencyId: string): boolean {
    const value = this.formControl.value as SelectedCompetency[] | null;
    if (!Array.isArray(value)) return false;
    return value.some((c) => c.matchCompetencyId === competencyId);
  }

  /**
   * True when the user has hit the selection cap and this option is
   * currently un-selected. Selected options stay enabled so the user
   * can untick them to make room.
   */
  isDisabled(competencyId: string): boolean {
    if (this.isSelected(competencyId)) return false;
    const max = (this.props['maxSelections'] as number | undefined) ?? 5;
    const current = this.formControl.value as SelectedCompetency[] | null;
    const length = Array.isArray(current) ? current.length : 0;
    return length >= max;
  }

  /**
   * After `toggleCompetency` runs, explicitly sync the native checkbox
   * DOM state to the form value. Needed because the browser toggles
   * `.checked` on click and Angular's `[checked]` property binding
   * won't re-apply on the next CD if its last-bound value is unchanged
   * (which is what happens when we refuse the add due to the max-cap).
   */
  onCheckboxChange(event: Event, option: CompetencyOption): void {
    this.toggleCompetency(option);
    const input = event.target as HTMLInputElement | null;
    if (input) input.checked = this.isSelected(option.id);
  }

  toggleCompetency(option: CompetencyOption): void {
    const currentValue = (this.formControl.value as SelectedCompetency[]) || [];
    const existingIndex = currentValue.findIndex(
      (c) => c.matchCompetencyId === option.id
    );

    if (existingIndex !== -1) {
      // Remove
      const newValue = currentValue.filter(
        (c) => c.matchCompetencyId !== option.id
      );
      this.formControl.setValue(newValue);
    } else {
      // Add (check max)
      const maxSelections = this.props["maxSelections"] || 5;
      if (currentValue.length >= maxSelections) {
        return;
      }

      const newCompetency: SelectedCompetency = {
        matchCompetencyId: option.id,
        name: option.name,
        description: option.description,
        sort: currentValue.length + 1,
      };

      this.formControl.setValue([...currentValue, newCompetency]);
    }
  }

  moveUp(index: number): void {
    if (index <= 0) return;

    const value = [...(this.formControl.value as SelectedCompetency[])];
    const currentSort = value[index].sort;
    value[index] = { ...value[index], sort: value[index - 1].sort };
    value[index - 1] = { ...value[index - 1], sort: currentSort };

    this.formControl.setValue(value.sort((a, b) => a.sort - b.sort));
  }

  moveDown(index: number): void {
    const value = [...(this.formControl.value as SelectedCompetency[])];
    if (index >= value.length - 1) return;

    const currentSort = value[index].sort;
    value[index] = { ...value[index], sort: value[index + 1].sort };
    value[index + 1] = { ...value[index + 1], sort: currentSort };

    this.formControl.setValue(value.sort((a, b) => a.sort - b.sort));
  }
}
